/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Frequently Asked Questions section for AI Removal
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI video subtitle remover?',
      answer:
        'An AI video subtitle remover is a tool that uses artificial intelligence to detect and erase hardcoded subtitles, captions, or text directly from video frames. Instead of blurring or covering them, FalcoCut rebuilds the background so your video looks natural and clean, ready to share without distractions.',
    },
    {
      id: 'faq-2',
      question: 'How to remove subtitles from a video?',
      answer: (
        <>
          <p className="mb-3">
            <strong>Step 1: Upload Your Video File</strong>
            <br />
            Open FalcoCut in your browser and import the video you want to clean up.
          </p>
          <p className="mb-3">
            <strong>Step 2: Select the Subtitle Area</strong>
            <br />
            Use the selection box to cover the subtitles you want removed. FalcoCut&apos;s
            AI will detect the text region automatically, and you can fine-tune the marked
            area before running the cleanup.
          </p>
          <p className="mb-3">
            <strong>Step 3: Review the AI Cleanup</strong>
            <br />
            Hit Start and let the system reconstruct the background where the subtitles
            were. Check the preview to confirm the text has been fully cleared and the
            frame looks natural.
          </p>
          <p>
            <strong>Step 4: Download the Finished Video</strong>
            <br />
            When you&apos;re satisfied with the result, export the polished video in high
            quality. Your subtitle-free clip is ready for posting, editing, or reuse.
          </p>
        </>
      ),
    },
    {
      id: 'faq-3',
      question: 'What types of text can FalcoCut AI Removal erase?',
      answer:
        'FalcoCut can erase more than just subtitles. It removes captions, watermarks, logos, and other on-screen text that distracts from your footage. The AI rebuilds the background so the video looks smooth and consistent, helping you repurpose clips without needing to re-record or manually edit.',
    },
    {
      id: 'faq-4',
      question: 'Is subtitle removal accurate across different languages?',
      answer:
        "Yes. FalcoCut's AI is trained to detect subtitles in many languages, not just English. It recognizes text patterns across scripts and fonts, making it useful for global projects. Accuracy is high because the tool focuses on visual detection, so subtitles are removed cleanly regardless of the language used.",
    },
    {
      id: 'faq-5',
      question: 'What video formats can I upload?',
      answer:
        'FalcoCut supports widely used video formats, including MP4 and MOV. These formats cover most social media clips, tutorials, and professional footage. By focusing on common standards, FalcoCut ensures smooth uploads and reliable processing without requiring you to convert files before editing.',
    },
    {
      id: 'faq-6',
      question: 'Is there a file size limit?',
      answer:
        'Yes. FalcoCut supports videos up to 10 minutes in length and files as large as 2GB. You can also process videos in resolutions up to 4K, so even long or high-quality projects are fully supported. This balance ensures smooth performance while giving you the flexibility to edit professional-grade content without compromise.',
    },
    {
      id: 'faq-7',
      question: 'Can FalcoCut handle longer videos or series?',
      answer:
        'Yes. FalcoCut can process longer clips or episodes as long as they fit within the supported limits. Videos up to 10 minutes in length, with file sizes up to 2GB, and resolutions up to 4K are supported. This makes it practical for training sessions, lectures, or multi-part series. You can clean each file individually and keep the quality consistent across your entire project.',
    },
    {
      id: 'faq-8',
      question: 'Can I use the AI Removal without installing software?',
      answer:
        "Yes. FalcoCut works directly in your browser, so there's no need to download or install any software. This makes it easy to access from desktop or mobile devices. You simply upload your video, let the AI process it, and download the cleaned result—all online and hassle-free.",
    },
    {
      id: 'faq-9',
      question: 'How fast does FalcoCut process videos?',
      answer:
        'FalcoCut is designed for speed. Most short clips are processed in seconds, while longer videos take only a few minutes depending on file size. The AI is optimized to detect and erase text quickly, so you spend less time waiting and more time reusing your content across platforms.',
    },
    {
      id: 'faq-10',
      question: "Can FalcoCut's AI Removal also generate or translate subtitles?",
      answer:
        'No. FalcoCut video subtitle remover is designed only to erase subtitles, captions, and on-screen text from your video files. However, if you need to generate or translate subtitles, FalcoCut also offers a video translator tool. With this companion feature, you can create and translate subtitles in 30+ languages, complete with natural dubbing and lip sync, making your content ready for global audiences.',
    },
    {
      id: 'faq-11',
      question: 'How to remove watermarks from a video?',
      answer: (
        <>
          <p className="mb-3">
            <strong>Step 1: Import Your Video</strong>
            <br />
            Open FalcoCut and load your video, then scrub through the timeline to locate
            every spot where the watermark appears. This helps you plan the cleanup more
            accurately.
          </p>
          <p className="mb-3">
            <strong>Step 2: Create a Mask Over the Watermark</strong>
            <br />
            Use the masking tool to draw a shape over the watermark. Adjust the
            mask&apos;s size so the edges blend naturally with the surrounding frame.
          </p>
          <p className="mb-3">
            <strong>Step 3: Let the AI Clean the Frame</strong>
            <br />
            Start the AI repair process. FalcoCut analyzes the masked region and rebuilds
            the background using nearby pixels. Review the frame-by-frame preview to
            ensure the fix stays consistent.
          </p>
          <p>
            <strong>Step 4: Save Your Clean Video</strong>
            <br />
            Once the result looks right, export the processed clip. Your watermark-free
            video is ready for publishing or further editing.
          </p>
        </>
      ),
    },
    {
      id: 'faq-12',
      question: 'How to remove logos from your video?',
      answer: (
        <>
          <p className="mb-3">
            <strong>Step 1: Upload the Clip With the Logo</strong>
            <br />
            Launch FalcoCut and upload the video that contains the logo you want to
            remove.
          </p>
          <p className="mb-3">
            <strong>Step 2: Select the Logo Area</strong>
            <br />
            Use the removal tool to draw a box around the logo. Adjust the boundaries so
            the entire graphic is included, ensuring the cleanup blends seamlessly.
          </p>
          <p className="mb-3">
            <strong>Step 3: Run the Logo Removal</strong>
            <br />
            Click Start and let FalcoCut&apos;s AI fill in the background where the logo
            used to be. Play through the clip to confirm the removal stays consistent,
            especially during motion or scene changes.
          </p>
          <p>
            <strong>Step 4: Export the Final Version</strong>
            <br />
            If everything looks natural, export the processed video. The logo is gone, and
            your clip is ready for reuse, repurposing, or distribution.
          </p>
        </>
      ),
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
